'use client';

import styles from './AdminChatHeader.module.css';
import type { AdminChatHeaderProps } from './AdminChatHeader.types';
import { AdminConversationsContext } from '@/_contexts/admin/AdminConversationsProvider';
import {
  Avatar,
  Flex,
  Text,
  Button,
  ButtonGroup,
  Tooltip,
} from '@/_components';
import { useContext, useMemo, useState, type FC } from 'react';
import { getAllConversationNames } from '@/_utils/conversations';
import { CompanyContext } from '@/_contexts/admin/CompanyProvider';
import { useRouter } from 'next/navigation';
import { EditParticipantsDialog } from './_components/EditParticipantsDialog';
import { UserContext } from '@/_contexts/UserProvider';
import { UserPreviewContext } from '@/_contexts/admin/UserPreviewProvider';
import type { Conversation } from '@/_types/models/conversations';
import { ToastContext } from '@/_contexts/ToastProvider';
import { getHasPermission } from '@/_utils/permissions';
import { PermissionsMapEnum } from '@/_types/permissions';

export const AdminChatHeader: FC<AdminChatHeaderProps> = ({ isPreview }) => {
  const [showParticipants, setShowParticipants] = useState<boolean>(false);

  const {
    selectedMessage,
    onToggleExporting,
    markConversationAsUnseen,
    reloadConversations,
  } = useContext(AdminConversationsContext);
  const { selectedCompany } = useContext(CompanyContext);
  const { previewConversation } = useContext(UserPreviewContext);
  const { permissions, isPartner, isFull } = useContext(UserContext);
  const { addToast } = useContext(ToastContext);
  const { push } = useRouter();

  const conversation: Conversation | undefined = useMemo(
    () => (isPreview ? previewConversation : selectedMessage),
    [isPreview, selectedMessage, previewConversation],
  );

  const isFilteredView: boolean = !!selectedCompany?.id;
  const backPath: string = '/messages';
  const backValue: string = 'Back to All Conversations';

  const conversationNames = getAllConversationNames(conversation);

  const onInboxClick = () => push(backPath);

  const onToggleParticipants = () => setShowParticipants(!showParticipants);

  const onMarkAsUnseen = () => {
    if (conversation) {
      markConversationAsUnseen(conversation.id);
      addToast('success', 'Conversation marked as unseen');
      reloadConversations();
      onInboxClick();
    }
  };

  const hasEditConversationPermission: boolean = getHasPermission(
    PermissionsMapEnum['conversation.edit'],
    permissions,
  );

  return (
    <>
      <EditParticipantsDialog
        isOpen={showParticipants}
        onClose={onToggleParticipants}
        conversation={conversation}
      />
      <Flex className={styles.adminChatHeader} isBetween={true}>
        {conversation && (
          <Flex isYCentered={true} gap={4}>
            {isFilteredView && (
              <ButtonGroup>
                <Button
                  onClick={onInboxClick}
                  value={backValue}
                  notificationCount={0}
                  iconLeft="caret-left"
                  size="small"
                  isDisabled={isPreview}
                  isIconOnly={true}
                  withTooltip={true}
                  intent="secondary"
                />
              </ButtonGroup>
            )}
            <Flex gap={2}>
              {conversationNames?.map((name, key) => {
                const isAdvisorType: boolean =
                  conversation?.advisor_names?.includes(name);

                return (
                  <Tooltip key={key}>
                    <Tooltip.Content>
                      <Text type="small" color="currentcolor" isEllipsis={true}>
                        {name}
                      </Text>
                    </Tooltip.Content>
                    <Tooltip.Trigger>
                      <Avatar
                        type={isAdvisorType ? 'firm' : 'user'}
                        size={32}
                        name={name}
                      />
                    </Tooltip.Trigger>
                  </Tooltip>
                );
              })}
            </Flex>
          </Flex>
        )}
        {conversation && (
          <ButtonGroup>
            {(isPartner || isFull || hasEditConversationPermission) && (
              <Button
                size="small"
                iconLeft="user-circle-gear"
                intent="ghost"
                withTooltip={true}
                isDisabled={isPreview}
                onClick={onToggleParticipants}
                isIconOnly={true}
                value="Add or remove participants"
              />
            )}
            <Button
              size="small"
              iconLeft="eye"
              intent="ghost"
              isDisabled={isPreview || !conversation.messages?.length}
              withTooltip={true}
              onClick={onMarkAsUnseen}
              isIconOnly={true}
              value="Mark as unseen"
            />
            <Button
              size="small"
              iconLeft="download"
              intent="ghost"
              isDisabled={isPreview}
              withTooltip={true}
              onClick={onToggleExporting}
              isIconOnly={true}
              value="Export conversation"
            />
          </ButtonGroup>
        )}
      </Flex>
    </>
  );
};
